import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import '../styles/AuthPage.css'

// ── Shared validation rules (mirror of backend) ──
function validateUsername(username) {
  if (!username || username.trim().length === 0) return 'Username is required.'
  if (username.trim().length < 3)  return 'Username must be at least 3 characters.'
  if (username.trim().length > 10) return 'Username must be 10 characters or fewer.'
  if (!/^[A-Za-z0-9]+$/.test(username.trim())) return 'Only letters and numbers — no spaces or special characters.'
  return null
}

export default function AuthPage({ onAuthSuccess, isModal = false, onClose }) {
  const [tab, setTab]           = useState('login')
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [fraction, setFraction] = useState(null)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState(null)
  const [success, setSuccess]   = useState(null)
  const [resetEmail, setResetEmail] = useState('')

  // Live username hint (only shown after first blur)
  const [usernameTouched, setUsernameTouched] = useState(false)
  const usernameError = usernameTouched ? validateUsername(username) : null

  useEffect(() => {
    if (isModal) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [isModal])

  useEffect(() => {
    if (!isModal) return
    const handler = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isModal, onClose])

  const resetForm = () => {
    setEmail(''); setPassword(''); setUsername(''); setFraction(null)
    setError(null); setSuccess(null); setUsernameTouched(false); setAgreedToTerms(false)
    setResetEmail('')
  }

  const handleForgotPassword = async () => {
    if (!resetEmail) return setError('Enter your email address.')
    setLoading(true); setError(null)
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    setLoading(false)
    if (error) return setError(error.message)
    setSuccess('Reset link sent! Check your email.')
  }

  const handleLogin = async () => {
    if (!email || !password) return setError('Please fill in all fields.')
    setLoading(true); setError(null)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (error) return setError(error.message)
    onAuthSuccess?.()
  }

  const handleRegister = async () => {
    // ── Frontend validation ──
    if (!email || !password) return setError('Please fill in all fields.')
    if (password.length < 6)  return setError('Password must be at least 6 characters.')
    if (!fraction)            return setError('Please choose your faction.')

    const unameErr = validateUsername(username)
    if (unameErr) { setUsernameTouched(true); return setError(unameErr) }
    if (!agreedToTerms) return setError('You must agree to the Terms of Service and Privacy Policy.')

    setLoading(true); setError(null)

    // ── Sign up with Supabase ──
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/?confirmed=true` }
    })
    if (error) { setLoading(false); return setError(error.message) }

    // ── Backend validation (uniqueness etc.) ──
    try {
      const res = await fetch('/api/users/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id:  data.user.id,
          username: username.trim(),
          fraction,
        })
      })
      if (!res.ok) {
        const d = await res.json()
        setLoading(false)
        return setError(d.error || 'Failed to create profile.')
      }
    } catch {
      setLoading(false)
      return setError('Could not connect to the server.')
    }

    setLoading(false)
    setSuccess('Account created! Check your email to confirm.')
  }

  const card = (
    <div className="auth-card" onClick={e => e.stopPropagation()}>
      {isModal && (
        <button className="auth-close-btn" onClick={onClose} aria-label="Close">✕</button>
      )}

      <div className="auth-header">
        <h1 className="auth-logo">AZERODLE</h1>
        <p className="auth-tagline1">World of Warcraft</p>
        <p className="auth-tagline2">Daily Challenge</p>
      </div>

      <div className="auth-tabs">
        <button className={`auth-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => { setTab('login'); resetForm() }}>
          Log In
        </button>
        <button className={`auth-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => { setTab('register'); resetForm() }}>
          Register
        </button>
      </div>

      <div className="auth-body">
        {tab === 'login' ? (
          <>
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input className="input" type="email" placeholder="your@email.com"
                value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            </div>
            <div className="auth-field">
              <label className="auth-label">Password</label>
              <input className="input" type="password" placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            </div>
            <button className="btn-gold" style={{width:'100%', marginTop:'0.5rem'}} onClick={handleLogin} disabled={loading}>
              {loading && <span className="loading-spinner" />}
              {loading ? 'Signing in...' : 'Enter'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.8rem' }}>
              <button className="auth-link-btn" onClick={() => { setTab('forgot'); resetForm() }}>
                Forgot password?
              </button>
            </p>
          </>
        ) : tab === 'forgot' ? (
          <>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted, #aaa)', marginBottom: '1rem' }}>
              Enter your email and we'll send you a reset link.
            </p>
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                className="input"
                type="email"
                placeholder="your@email.com"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleForgotPassword()}
              />
            </div>
            <button className="btn-gold" style={{width:'100%', marginTop:'0.5rem'}} onClick={handleForgotPassword} disabled={loading}>
              {loading && <span className="loading-spinner" />}
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.8rem' }}>
              <button className="auth-link-btn" onClick={() => { setTab('login'); resetForm() }}>
                ← Back to Login
              </button>
            </p>
          </>
        ) : (
          <>
            <div className="auth-field">
              <label className="auth-label">
                Nick
                <span className="auth-label-hint">3–10 characters, letters &amp; numbers only</span>
              </label>
              <input
                className={`input ${usernameError ? 'input-error' : username && !usernameError ? 'input-ok' : ''}`}
                type="text"
                placeholder="Thrall"
                maxLength={10}
                value={username}
                onChange={e => setUsername(e.target.value)}
                onBlur={() => setUsernameTouched(true)}
              />
              {usernameError && (
                <span className="auth-field-error">{usernameError}</span>
              )}
            </div>
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input className="input" type="email" placeholder="your@email.com"
                value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="auth-field">
              <label className="auth-label">Password</label>
              <input className="input" type="password" placeholder="min. 6 characters"
                value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            <div className="auth-field">
              <label className="auth-label">Faction</label>
              <div className="auth-fraction">
                <div
                  className={`fraction-btn alliance ${fraction === 'Alliance' ? 'selected' : ''}`}
                  onClick={() => setFraction('Alliance')}
                >
                  <img src="/alliance.png" alt="Alliance" className="fraction-img" />
                </div>
                <div
                  className={`fraction-btn horde ${fraction === 'Horde' ? 'selected' : ''}`}
                  onClick={() => setFraction('Horde')}
                >
                  <img src="/horde.png" alt="Horde" className="fraction-img" />
                </div>
              </div>
            </div>
            <div className="auth-terms-row">
              <label className="auth-terms-label">
                <input
                  type="checkbox"
                  className="auth-terms-checkbox"
                  checked={agreedToTerms}
                  onChange={e => setAgreedToTerms(e.target.checked)}
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                  I confirm I am at least 16 years old.
                </span>
              </label>
            </div>
            <button className="btn-gold" style={{width:'100%', marginTop:'0.5rem'}} onClick={handleRegister} disabled={loading}>
              {loading && <span className="loading-spinner" />}
              {loading ? 'Creating account...' : 'Create'}
            </button>
          </>
        )}
        {error   && <div className="auth-error">⚠ {error}</div>}
        {success && <div className="auth-success">✔ {success}</div>}
      </div>

      <div className="auth-footer">For the Horde · For the Alliance</div>
    </div>
  )

  if (isModal) {
    return (
      <div className="auth-modal-backdrop">
        {card}
      </div>
    )
  }

  return (
    <div className="page-bg auth-root">
      <div className="auth-overlay" />
      {card}
    </div>
  )
}